import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  ChevronRight,
  Plus,
  Check,
  Package,
  Sparkles,
  ArrowUpDown,
  Tag,
  Globe,
  Flame,
  Layers,
  ShoppingBag,
} from 'lucide-react'
import { api } from '@/lib/api'
import { formatPrice, localizedName } from '@/lib/format'
import { useT } from '@/lib/i18n'
import { useAppStore } from '@/lib/store'
import { usePageTitle } from '@/lib/usePageTitle'
import { haptic } from '@/lib/telegram'
import { Markdown } from '@/components/Markdown'
import type { SpecShowcase, ProductSummary } from '@/shared/types'

type SortOption = 'popular' | 'price_asc' | 'price_desc' | 'newest'

function renderSpecIcon(code: string, className = 'h-4 w-4') {
  const norm = code.toLowerCase()
  if (norm.includes('note') || norm.includes('нот')) return <Flame className={className} />
  if (norm.includes('group') || norm.includes('груп') || norm.includes('family'))
    return <Layers className={className} />
  if (norm.includes('country') || norm.includes('країн') || norm.includes('стран'))
    return <Globe className={className} />
  if (norm.includes('collection') || norm.includes('колекц')) return <Tag className={className} />
  return <Sparkles className={className} />
}


export function SpecShowcasePage() {
  const { specCode, valueSlug } = useParams({ strict: false }) as {
    specCode?: string
    valueSlug?: string
  }
  const T = useT('specs')
  const lang = useAppStore((s) => s.lang)
  const setCartCount = useAppStore((s) => s.setCartCount)
  const qc = useQueryClient()

  const [sortBy, setSortBy] = useState<SortOption>('popular')
  const [addedItemIds, setAddedItemIds] = useState<Set<number>>(new Set())
  const [isDescExpanded, setIsDescExpanded] = useState(false)

  const { data: specData, isLoading, isError } = useQuery<SpecShowcase>({
    queryKey: ['spec-showcase', specCode, valueSlug],
    queryFn: () => api.get<SpecShowcase>(`/specs/${specCode}/${valueSlug}`),
    enabled: Boolean(specCode && valueSlug),
    staleTime: 300_000,
  })

  const specTitle = useMemo(() => {
    if (!specData) return ''
    if (lang === 'en') return specData.title_en || specData.value_raw
    if (lang === 'ru') return specData.title_ru || specData.value_raw
    return specData.title_uk || specData.value_raw
  }, [specData, lang])

  const specName = useMemo(() => {
    if (!specData) return ''
    return lang === 'en' ? specData.spec_name_en : specData.spec_name_uk
  }, [specData, lang])

  const specDescription = useMemo(() => {
    if (!specData) return ''
    if (lang === 'en') return specData.description_en
    if (lang === 'ru') return specData.description_ru
    return specData.description_uk
  }, [specData, lang])

  usePageTitle(specTitle ? `${specTitle} — ${specName}` : T.title)

  const addItemMutation = useMutation({
    mutationFn: async (productId: number) => {
      await api.post('/cart/add', { product_id: productId, qty: 1 })
      return productId
    },
    onSuccess: async (productId) => {
      haptic('success')
      setAddedItemIds((prev) => new Set(prev).add(productId))
      try {
        const cart = await api.get<{ count: number }>('/cart')
        if (typeof cart?.count === 'number') {
          setCartCount(cart.count)
        }
        void qc.invalidateQueries({ queryKey: ['cart'] })
      } catch {
        // ignore
      }
      setTimeout(() => {
        setAddedItemIds((prev) => {
          const next = new Set(prev)
          next.delete(productId)
          return next
        })
      }, 2000)
    },
    onError: () => {
      haptic('error')
    },
  })

  function handleQuickAdd(e: React.MouseEvent, product: ProductSummary) {
    e.preventDefault()
    e.stopPropagation()
    addItemMutation.mutate(product.id)
  }

  // Sort products
  const sortedProducts = useMemo(() => {
    if (!specData?.products) return []

    const list = [...specData.products]

    if (sortBy === 'price_asc') {
      list.sort((a, b) => a.price_uah - b.price_uah)
    } else if (sortBy === 'price_desc') {
      list.sort((a, b) => b.price_uah - a.price_uah)
    } else if (sortBy === 'newest') {
      list.sort((a, b) => b.id - a.id)
    }

    return list
  }, [specData, sortBy])

  // Limit preview to maximum 10 products
  const displayProducts = useMemo(() => {
    return sortedProducts.slice(0, 10)
  }, [sortedProducts])

  if (isLoading) {

    return (
      <div className="min-h-screen bg-[#1a202c] p-4 text-[#f8f3ea]">
        <div className="mx-auto max-w-5xl animate-pulse space-y-4">
          <div className="h-6 w-32 rounded-lg bg-[#28303c]" />
          <div className="h-44 rounded-2xl bg-[#28303c]" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="h-64 rounded-2xl bg-[#28303c]" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (isError || !specData) {
    return (
      <div className="min-h-screen bg-[#1a202c] px-4 py-12 text-center text-[#f8f3ea]">
        <div className="mx-auto max-w-md rounded-2xl border border-[#3a4352] bg-[#242c38] p-8">
          <Tag className="mx-auto h-12 w-12 text-slate-500 mb-3" />
          <h2 className="text-lg font-bold text-[#f8f3ea]">{T.noProducts}</h2>
          <Link
            to="/catalog"
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-[#c9a24b] px-4 py-2 text-xs font-bold text-[#1a202c] hover:bg-[#d8b25c] transition"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {T.backToCatalog}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#1a202c] pb-24 text-[#f8f3ea]">
      {/* Top Bar / Breadcrumbs */}
      <div className="border-b border-[#2d3748] bg-[#1a202c]/90 backdrop-blur-md sticky top-0 z-20">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 overflow-hidden text-xs text-slate-400">
            <Link to="/" className="hover:text-amber-400 transition shrink-0">
              Головна
            </Link>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-600" />
            <Link to="/catalog" className="hover:text-amber-400 transition shrink-0">
              Каталог
            </Link>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-600" />
            <span className="truncate font-medium text-[#c9a24b]">{specTitle}</span>
          </div>
          <Link
            to="/catalog"
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-amber-400 transition shrink-0"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>{T.backToCatalog}</span>
          </Link>
        </div>
      </div>

      <main className="mx-auto max-w-5xl px-4 pt-4 space-y-6">
        {/* Hero Banner */}
        <section className="relative overflow-hidden rounded-3xl border border-[#3a4352] bg-gradient-to-br from-[#242c38] via-[#1f2732] to-[#171d26] p-6 sm:p-8 shadow-xl">
          <div className="absolute top-0 right-0 -mt-8 -mr-8 h-48 w-48 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-3">
              {/* Type Badge */}
              <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-300">
                {renderSpecIcon(specCode || '', 'h-3.5 w-3.5')}
                <span>{specName}</span>
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#f8f3ea]">
                {specTitle}
              </h1>

              {/* Description */}
              {specDescription && (
                <div className="max-w-2xl text-sm leading-relaxed text-slate-300">
                  <div className={`leading-relaxed ${!isDescExpanded && specDescription.length > 280 ? 'line-clamp-3' : ''}`}>
                    <Markdown content={specDescription} className="space-y-2 leading-relaxed" />
                  </div>
                  {specDescription.length > 280 && (
                    <button
                      type="button"
                      onClick={() => setIsDescExpanded((v) => !v)}
                      className="mt-1.5 font-semibold text-amber-400 hover:underline text-xs"
                    >
                      {isDescExpanded ? T.showLess : T.readMore}
                    </button>
                  )}
                </div>
              )}

              {/* Stats Badge */}
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <div className="inline-flex items-center gap-1.5 rounded-xl border border-slate-700/60 bg-slate-800/60 px-3 py-1.5 text-xs text-slate-300">
                  <Package className="h-3.5 w-3.5 text-amber-400" />
                  <span>{T.productCount(specData.products_count)}</span>
                </div>
              </div>
            </div>

            {/* Showcase Image or Big Icon */}
            {specData.image_url ? (
              <div className="h-28 w-28 sm:h-36 sm:w-36 shrink-0 overflow-hidden rounded-2xl border border-amber-500/20 bg-slate-900/60 shadow-lg">
                <img
                  src={specData.image_url}
                  alt={specTitle}
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <div className="flex h-24 w-24 sm:h-28 sm:w-28 shrink-0 items-center justify-center rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-amber-500/5 text-amber-400 shadow-inner">
                {renderSpecIcon(specCode || '', 'h-12 w-12')}
              </div>
            )}

          </div>
        </section>

        {/* Toolbar: Sorting & Catalog Link */}
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#2d3748] bg-[#242c38]/70 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-400">{T.productsWithSpec(specTitle)}:</span>
            <span className="text-xs font-bold text-amber-400">{sortedProducts.length}</span>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            {/* Sort Selector */}
            <div className="flex items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800/90 px-2.5 py-1.5 text-xs text-slate-200">
              <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                aria-label={T.sortBy}
                className="bg-transparent text-xs text-slate-200 focus:outline-none cursor-pointer"
              >
                <option value="popular" className="bg-[#1a202c]">
                  {T.sortBy}
                </option>
                <option value="price_asc" className="bg-[#1a202c]">
                  {T.sortPriceAsc}
                </option>
                <option value="price_desc" className="bg-[#1a202c]">
                  {T.sortPriceDesc}
                </option>
                <option value="newest" className="bg-[#1a202c]">
                  {T.sortNewest}
                </option>
              </select>
            </div>

            <Link
              to="/catalog"
              search={{ attr: specData.spec_code, value: specData.value_raw }}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-xl bg-amber-500/10 border border-amber-500/30 px-3 py-1.5 text-xs font-semibold text-amber-300 hover:bg-amber-500/20 transition"
            >
              <ShoppingBag className="h-3.5 w-3.5" />
              <span>{T.viewAllInCatalog(specData.products_count)}</span>
            </Link>
          </div>
        </section>

        {/* Products Grid */}
        {sortedProducts.length === 0 ? (
          <div className="rounded-2xl border border-[#2d3748] bg-[#242c38] p-12 text-center">
            <Package className="mx-auto h-12 w-12 text-slate-600 mb-2" />
            <p className="text-sm text-slate-400">{T.noProducts}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5">
              {displayProducts.map((product) => {
                const pName = localizedName(lang, product.name_uk, product.name_en)
                const pCat = localizedName(
                  lang,
                  product.category_name_uk || '',
                  product.category_name_en || ''
                )
                const isAdded = addedItemIds.has(product.id)
                const isAdding =
                  addItemMutation.isPending && addItemMutation.variables === product.id

                return (
                  <Link
                    key={product.id}
                    to="/catalog/$productSlug"
                    params={{ productSlug: product.slug || String(product.id) }}
                    className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-[#2d3748] bg-[#242c38] p-3 transition hover:border-amber-500/40 hover:bg-[#283240] hover:shadow-lg"
                  >

                    <div>
                      {/* Image */}
                      <div className="aspect-square w-full overflow-hidden rounded-xl bg-[#1a202c]">
                        {product.photo_url ? (
                          <img
                            src={product.photo_url}
                            alt={pName}
                            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-slate-600">
                            <Package className="h-10 w-10" />
                          </div>
                        )}
                      </div>

                      {/* Meta */}
                      <div className="mt-2.5 space-y-1">
                        {pCat && (
                          <span className="block truncate text-[10px] font-medium uppercase tracking-wider text-amber-400/80">
                            {pCat}
                          </span>
                        )}
                        <h3 className="line-clamp-2 text-xs font-semibold leading-tight text-[#f8f3ea] group-hover:text-amber-300 transition">
                          {pName}
                        </h3>
                      </div>
                    </div>

                    {/* Price & Action */}
                    <div className="mt-3 flex items-center justify-between pt-2 border-t border-slate-700/50">
                      <div>
                        <span className="text-xs font-bold text-amber-400">
                          {formatPrice(product.price_uah)}
                        </span>
                      </div>

                      <button
                        type="button"
                        aria-label="Add to cart"
                        onClick={(e) => handleQuickAdd(e, product)}
                        disabled={isAdding || isAdded}
                        className={`flex h-7 w-7 items-center justify-center rounded-lg transition ${
                          isAdded
                            ? 'bg-emerald-500 text-white'
                            : 'bg-[#c9a24b] text-[#1a202c] hover:bg-[#d8b25c] active:scale-95'
                        }`}
                      >
                        {isAdded ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Plus className="h-4 w-4 stroke-[2.5]" />
                        )}
                      </button>
                    </div>
                  </Link>
                )
              })}
            </div>

            {/* View All In Catalog Button */}
            {specData.products.length > 0 && (
              <div className="mt-8 text-center">
                <Link
                  to="/catalog"
                  search={{ attr: specData.spec_code, value: specData.value_raw }}
                  className="inline-flex items-center gap-2 rounded-2xl border border-amber-500 bg-amber-500/10 px-6 py-3 text-sm font-bold text-amber-300 hover:bg-amber-500 hover:text-[#1a202c] transition shadow-md"
                >
                  <span>{T.viewAllInCatalog(specData.products_count)}</span>
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
